import { useParams } from 'react-router-dom'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getItemProcesses } from '../../services'
import { ArrowRightLeft, Package } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Link } from 'react-router-dom'
import { paths } from '@/utils/path'

const ItemProcesses = () => {
    const { id } = useParams<{ id: string }>()

    const { data: processesData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_PROCESSES, 'item', id],
        queryFn: () => getItemProcesses(id!),
    })

    const processes = processesData?.results || []

    // Helper to get process type badge variant
    const getProcessTypeBadgeVariant = (type: string) => {
        switch (type) {
            case 'import_purchase':
            case 'import_return':
                return 'default'
            case 'export_sale':
                return 'destructive'
            case 'export_loan':
            case 'export_delivery':
                return 'secondary'
            case 'internal_transfer':
                return 'outline'
            default:
                return 'default'
        }
    }

    // Helper to get status badge variant
    const getStatusBadgeVariant = (status: string) => {
        switch (status) {
            case 'completed':
                return 'default'
            case 'approved':
            case 'pending_approval':
                return 'secondary'
            case 'cancelled':
                return 'destructive'
            case 'draft':
                return 'outline'
            default:
                return 'outline'
        }
    }

    // Helper to format process type
    const formatProcessType = (type: string) => {
        const typeMap: Record<string, string> = {
            import_purchase: 'Import (Purchase)',
            import_return: 'Import (Return)',
            export_sale: 'Export (Sale)',
            export_loan: 'Export (Loan)',
            export_delivery: 'Export (Delivery)',
            internal_transfer: 'Internal Transfer',
            inventory_check: 'Inventory Check',
        }
        return typeMap[type] || type
    }

    // Helper to format date
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return 'N/A'
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    if (processes.length === 0) {
        return (
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Linked Processes
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-muted-foreground">
                        No processes associated with this item
                    </p>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className="mt-2">
            <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Linked Processes
                    <Badge variant="outline" className="ml-auto">
                        {processes.length}
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Process Code</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Route</TableHead>
                                <TableHead>Date</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {processes.map((process: any) => (
                                <TableRow key={process.id}>
                                    <TableCell className="font-medium">
                                        <Link
                                            to={`${paths.warehouseProcesses}/${process.id}`}
                                            className="text-blue-600 hover:underline"
                                        >
                                            {process.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={getProcessTypeBadgeVariant(
                                                process.type
                                            )}
                                        >
                                            {formatProcessType(process.type)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={getStatusBadgeVariant(
                                                process.status
                                            )}
                                        >
                                            {process.status?.toUpperCase() ||
                                                ''}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        <div className="flex items-center gap-1">
                                            <span className="text-muted-foreground">
                                                {process.source?.name ||
                                                    'External'}
                                            </span>
                                            <ArrowRightLeft className="w-3 h-3 text-muted-foreground" />
                                            <span className="text-muted-foreground">
                                                {process.destination?.name ||
                                                    'External'}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {formatDate(process.created_at)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    )
}

export default ItemProcesses
